import React from "react";
import { Avatar, Card, Flex, Progress, Timeline, Typography } from "antd";
import { UserOutlined, MailOutlined } from "@ant-design/icons";
import { IndexInfoLabel } from "./IndexTooltip";

const { Text } = Typography;

const initials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();

// Right-column profile card for the Candidate Profile detail view, stacked
// above the Individual Profile Summary card — avatar, name + employee ID,
// role, current SRI, previous roles, contact details. An antd Card (not a
// plain div) so it reads as one of a matched pair with the Individual
// Profile Summary card directly below it, rather than a plainer element
// sitting on top of a polished one. showPreviousRoles={false} hides the
// Previous roles timeline for entry points that don't need it (PAIC, View Planning).
const CandidateProfileCard = ({ profile, showPreviousRoles = true }) => {
  if (!profile) return null;

  return (
    <Card style={{ flexShrink: 0 }}>
      <Flex vertical align="center" gap={2}>
        <Avatar size={72} icon={<UserOutlined />}>{initials(profile.name)}</Avatar>
        <Text strong>{profile.name}{profile.employeeId ? ` — ${profile.employeeId}` : ""}</Text>
        <Text type="secondary">{profile.currentRoleTitle}</Text>
      </Flex>

      {profile.augmentationPotential != null && (
        <Flex vertical align="center" style={{ marginTop: 16 }}>
          <Text type="secondary"><IndexInfoLabel index="SRI">Current SRI</IndexInfoLabel></Text>
          <Progress
            percent={profile.augmentationPotential}
            strokeColor="#0155E7"
            format={(percent) => <Text strong>{percent}%</Text>}
          />
        </Flex>
      )}

      {showPreviousRoles && !!profile.previousRoles?.length && (
        <>
          <hr />
          <Text strong style={{ display: "block", margin: "12px 0 8px" }}>Previous roles</Text>
          {/* A timeline, not a bare list — each stint gets its own marker and
              its date range under the title, so repeat titles (the same role
              held twice) read as separate periods rather than duplicates. */}
          <Timeline
            items={profile.previousRoles.map((r, i) => ({
              key: i,
              color: "gray",
              content: (
                <Flex vertical>
                  <Text>{r.title}</Text>
                  {r.dateRange && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {r.dateRange}
                    </Text>
                  )}
                </Flex>
              ),
            }))}
          />
        </>
      )}

      {profile.contact && (
        <>
          <Text strong style={{ display: "block", margin: "12px 0 8px" }}>Contact Details</Text>
          {profile.contact.email && (
            <Flex align="center" gap={8}>
              <MailOutlined />
              <Text>{profile.contact.email}</Text>
            </Flex>
          )}
        </>
      )}
    </Card>
  );
};

export default CandidateProfileCard;
